import { apiClient, newIdempotencyKey } from './client'
import type { AccountView, CreateAccountRequest, PatchAccountRequest } from '../types/account'

/** The universal list-response shape (`CursorPage<T>` on the backend). */
export interface CursorPage<T> {
  items: T[]
  nextCursor: string | null
}

export interface ListAccountsParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  /** `field,-field2` — backend only allow-lists name/type/status/createdAt/id (AccountService.RSQL). */
  sort?: string | null
  cursor?: string | null
  /** Clamped to 200 server-side (Pagination.normalizeLimit); default page size here is 50. */
  limit?: number
}

function toQuery(params: ListAccountsParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

export async function listAccounts(
  params: ListAccountsParams,
  signal?: AbortSignal,
): Promise<CursorPage<AccountView>> {
  const { data } = await apiClient.get<CursorPage<AccountView>>(`/accounts?${toQuery(params)}`, {
    signal,
  })
  return data
}

export async function getAccount(
  id: string,
  signal?: AbortSignal,
): Promise<{ account: AccountView; version: number }> {
  const { data, version } = await apiClient.get<AccountView>(`/accounts/${id}`, { signal })
  return { account: data, version: version ?? data.version }
}

export async function createAccount(
  request: CreateAccountRequest,
): Promise<{ account: AccountView; version: number }> {
  const { data, version } = await apiClient.post<AccountView>('/accounts', request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return { account: data, version: version ?? data.version }
}

export async function patchAccount(
  id: string,
  request: PatchAccountRequest,
  ifMatchVersion: number,
): Promise<{ account: AccountView; version: number }> {
  const { data, version } = await apiClient.patch<AccountView>(`/accounts/${id}`, request, {
    ifMatchVersion,
  })
  return { account: data, version: version ?? data.version }
}
